import { useState, useMemo, useRef, useEffect } from "react";
import "./CalendarView.css";

const MONTHS   = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW      = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const PRI_CLR  = { high: "#c4553a", medium: "#d4963a", low: "#7a9a6b" };

// Returns YYYY-MM-DD in local time
function localDateStr(d) {
  return d.toLocaleDateString("en-CA");
}

function buildGrid(year, month) {
  const startDow    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array(startDow).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function getItemsForDay(date, todos, recurrences) {
  const dow = date.getDay();
  const ds  = localDateStr(date);

  // Actual todos whose due_date falls on this day (generated recurring)
  const dueTodos = todos.filter(t => t.due_date && t.due_date.slice(0, 10) === ds);
  const duedRecIds = new Set(dueTodos.filter(t => t.recurrence_id).map(t => t.recurrence_id));

  // Regular todos (no recurrence) created on this day
  const createdTodos = todos.filter(t =>
    !t.recurrence_id &&
    !t.due_date &&
    new Date(t.created_at).toLocaleDateString("en-CA") === ds
  );

  // Recurrences scheduled this day of week but no generated todo yet (future/upcoming)
  const projected = recurrences.filter(r =>
    r.active && r.days_of_week.includes(dow) && !duedRecIds.has(r.id)
  );

  return { dueTodos, createdTodos, projected };
}

function getDots(date, todos, recurrences) {
  const { dueTodos, createdTodos, projected } = getItemsForDay(date, todos, recurrences);
  const dots = [
    ...dueTodos.map(t    => ({ color: t.category_color || PRI_CLR[t.priority], key: `d${t.id}` })),
    ...projected.map(r   => ({ color: r.category_color || "#7a7570",           key: `p${r.id}` })),
    ...createdTodos.map(t => ({ color: PRI_CLR[t.priority],                    key: `c${t.id}` })),
  ];
  return dots.slice(0, 5);
}

export default function CalendarView({ todos, recurrences }) {
  const today = new Date();
  const [year,     setYear]     = useState(today.getFullYear());
  const [month,    setMonth]    = useState(today.getMonth());
  const [selected, setSelected] = useState(null);
  const detailRef = useRef(null);

  useEffect(() => {
    if (selected && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected]);

  const grid     = useMemo(() => buildGrid(year, month), [year, month]);
  const todayStr = localDateStr(today);

  const prev = () => {
    setSelected(null);
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const next = () => {
    setSelected(null);
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const detail = useMemo(() => {
    if (!selected) return null;
    const { dueTodos, createdTodos, projected } = getItemsForDay(selected, todos, recurrences);
    return { dueTodos, createdTodos, projected };
  }, [selected, todos, recurrences]);

  return (
    <div className="calendar-view">
      {/* Month navigation */}
      <div className="cal-nav">
        <button className="cal-nav-btn" onClick={prev}>‹</button>
        <span className="cal-month-label">
          {MONTHS[month]} <em>{year}</em>
        </span>
        <button className="cal-nav-btn" onClick={next}>›</button>
      </div>

      {/* Grid */}
      <div className="cal-grid">
        {DOW.map(d => <div key={d} className="cal-dow">{d}</div>)}

        {grid.map((date, i) => {
          if (!date) return <div key={`e${i}`} className="cal-cell empty" />;

          const ds       = localDateStr(date);
          const dots     = getDots(date, todos, recurrences);
          const isToday  = ds === todayStr;
          const isSel    = selected && localDateStr(selected) === ds;

          return (
            <div
              key={ds}
              className={`cal-cell${isToday ? " today" : ""}${isSel ? " selected" : ""}`}
              onClick={() => setSelected(isSel ? null : date)}
            >
              <span className="cal-day-num">{date.getDate()}</span>
              {dots.length > 0 && (
                <div className="cal-dots">
                  {dots.map(dot => (
                    <span key={dot.key} className="cal-dot" style={{ background: dot.color }} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Day detail panel */}
      {selected && detail && (
        <div className="cal-detail" ref={detailRef}>
          <div className="cal-detail-header">
            {selected.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>

          {detail.dueTodos.length === 0 && detail.createdTodos.length === 0 && detail.projected.length === 0 ? (
            <p className="cal-detail-empty">Nothing scheduled</p>
          ) : (
            <div className="cal-detail-list">
              {detail.dueTodos.map(t => (
                <div key={t.id} className={`cal-detail-item${t.completed ? " done" : ""}`}>
                  <span className="cal-detail-dot" style={{ background: t.category_color || PRI_CLR[t.priority] }} />
                  <div className="cal-detail-text">
                    <span className="cal-detail-title">{t.title}</span>
                    {t.description && <span className="cal-detail-desc">{t.description}</span>}
                  </div>
                  <div className="cal-detail-tags">
                    {t.category_name && (
                      <span className="cal-tag cat-tag" style={{ color: t.category_color }}>{t.category_name}</span>
                    )}
                    {t.completed && <span className="cal-tag done-tag">done</span>}
                  </div>
                </div>
              ))}

              {detail.projected.map(r => (
                <div key={r.id} className="cal-detail-item projected">
                  <span className="cal-detail-dot" style={{ background: r.category_color || "#7a7570" }} />
                  <div className="cal-detail-text">
                    <span className="cal-detail-title">{r.title}</span>
                    {r.description && <span className="cal-detail-desc">{r.description}</span>}
                  </div>
                  <div className="cal-detail-tags">
                    {r.category_name && (
                      <span className="cal-tag cat-tag" style={{ color: r.category_color }}>{r.category_name}</span>
                    )}
                    <span className="cal-tag rec-tag">recurring</span>
                  </div>
                </div>
              ))}

              {detail.createdTodos.map(t => (
                <div key={t.id} className={`cal-detail-item${t.completed ? " done" : ""}`}>
                  <span className="cal-detail-dot" style={{ background: PRI_CLR[t.priority] }} />
                  <div className="cal-detail-text">
                    <span className="cal-detail-title">{t.title}</span>
                    {t.description && <span className="cal-detail-desc">{t.description}</span>}
                  </div>
                  <div className="cal-detail-tags">
                    {t.category_name && (
                      <span className="cal-tag cat-tag" style={{ color: t.category_color }}>{t.category_name}</span>
                    )}
                    {t.completed && <span className="cal-tag done-tag">done</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
